import { styled } from 'goober';
import { forwardRef } from 'react';

export const SideNavContainer = styled('div')(({ theme }) => ({
    flexShrink: 0,
    width: 'auto',
    zIndex: theme.zIndex.SIDE_NAV,
}));

export const SideNavWidget = styled('div')(({ theme }) => ({
    width: '100%',
    background: theme.colors.ui.dark,
    flexShrink: 0,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
}));

export const SideNavWrap = styled(SideNavWidget)<{
    $visible: boolean;
}>(({ theme, $visible }) => ({
    width: '220px',
    background: theme.branding.sidenav.background,
    flexShrink: 0,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    transform: $visible ? 'translate(0, 0)' : 'translate(-100%, 0)',
    transition: 'transform 300ms ease',
    position: 'absolute',
}));

export const SideNavHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginBottom: '16px',
}));

export const SideNavLogo = styled('div')(() => ({
    width: '120px',
    height: '100%',
    paddingTop: '3px',
    marginRight: '15px',
    marginLeft: '6px',
}));

export const SideNavTrigger = styled('div')(() => ({
    height: '64px',
    padding: '4px',
    position: 'absolute',
    left: '10px',
    display: 'flex',
    alignItems: 'center',
}));

export const SideNavClose = styled('div')(() => ({
    cursor: 'pointer',
    svg: { display: 'block', position: 'relative', top: '3px' },
}));

export const SideNavHoverZone = styled('div')(() => ({
    position: 'absolute',
    left: 0,
    top: 0,
    width: '16px',
    height: '64px',
}));

export const SideNavHoverHint = styled('div')<{
    $hint: boolean;
}>(({ theme, $hint }) => ({
    position: 'absolute',
    left: 0,
    top: '4px',
    width: '12px',
    height: '56px',
    transform: $hint ? 'none' : 'translate(-8px,0)',
    opacity: $hint ? 0.15 : 0,
    borderTopRightRadius: '3px',
    borderBottomRightRadius: '3px',
    background: theme.colors.neutral.w900,
    transition: 'transform 200ms ease, opacity 200ms ease',
}));

export const SideNavItemIconBox = styled('div')(() => ({
    position: 'relative',
    height: '24px',
    width: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

export const SideNavItemCheckBox = styled(SideNavItemIconBox)(() => ({}));

export const SideNavItemLabel = styled('label')(() => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
}));

export const SideNavHr = styled('hr')(({ theme }) => ({
    border: 'none',
    backgroundColor: theme.branding.sidenav.divider,
    opacity: 1,
    height: '1px',
    margin: '16px 0px',
}));

export const SideNavItemRootWrapper = styled('div', forwardRef)`
    display: flex;
    position: relative;
    transition: padding 200ms ease;

    .line {
        position: absolute;
        height: 24px;
        border-left: 1px solid ${(props) => props.theme.colors.ui.hoverline};
    }

    &:hover .line {
        border-left: 1px solid ${(props) => props.theme.colors.ui.hairline};
    }

    & ${SideNavItemIconBox}.whenHovered, &:hover ${SideNavItemIconBox}.whenNotHovered {
        display: none;
    }

    & ${SideNavItemIconBox}.whenNotHovered, &:hover ${SideNavItemIconBox}.whenHovered {
        display: unset;
    }
`;

export const SideNavItemWrapper = styled(
    'li',
    forwardRef
)<{ $canHover?: boolean; $highlight?: boolean }>(({ theme, $canHover, $highlight }) => ({
    listStyle: 'none',
    display: 'flex',
    position: 'relative',

    '.line': {
        position: 'absolute',
        height: '24px',
        borderLeft: `1px solid ${$highlight ? theme.branding.sidenav.lineHighlight : theme.branding.sidenav.line}`,
    },
    '&:hover .line': {
        borderLeft: $canHover
            ? `1px solid ${theme.branding.sidenav.lineHover}`
            : `1px solid ${$highlight ? theme.branding.sidenav.lineHighlight : theme.branding.sidenav.line}`,
    },
}));

export const SideNavItem = styled(
    'div',
    forwardRef
)<{ $highlight?: boolean; $canHover?: boolean; $dim?: boolean }>(({ theme, $highlight, $canHover, $dim }) => ({
    cursor: 'pointer',
    display: 'flex',
    height: '24px',
    color: theme.branding.sidenav.text,
    alignItems: 'center',
    background: $highlight ? theme.branding.sidenav.backgroundHighlight : 'transparent',
    borderRadius: '6px',
    width: '100%',
    paddingRight: '15px',
    userSelect: 'none',

    '&> *': {
        opacity: $dim ? 0.65 : 1,
        cursor: 'pointer',
    },
    '&:hover': {
        color: $canHover
            ? theme.branding.sidenav.textHover
            : $highlight
            ? theme.branding.sidenav.textHighlight
            : theme.branding.sidenav.text,
        background: $canHover
            ? theme.branding.sidenav.backgroundHover
            : $highlight
            ? theme.branding.sidenav.backgroundHighlight
            : 'transparent',
    },
}));

export const SideNavFooter = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: '6px',
    marginTop: '16px',
    flexDirection: 'column',
}));

export const SideNavResizer = styled('div')(() => ({
    width: '6px',
    height: '100%',
    position: 'absolute',
    top: 0,
    right: '-3px',
    cursor: 'col-resize',
}));

export const UserProfileMenuContainer = styled(
    'div',
    forwardRef
)<{ $hover?: boolean; $highlight?: boolean }>(({ theme, $hover, $highlight }) => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flexGrow: 1,
    borderRadius: '10px',
    paddingLeft: '8px',
    paddingRight: '8px',
    background: $highlight
        ? theme.branding.sidenav.backgroundHighlight
        : $hover
        ? theme.branding.sidenav.backgroundHover
        : theme.branding.sidenav.background,
    border: `1px solid ${theme.branding.sidenav.divider}`,
    height: '40px',
}));

export const UserName = styled('div')(({ theme }) => ({
    whiteSpace: 'nowrap',
    paddingLeft: '8px',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.branding.sidenav.text,
    fontWeight: '500',
}));

export const TenantName = styled('div')(({ theme }) => ({
    fontSize: '10px',
    whiteSpace: 'nowrap',
    paddingLeft: '8px',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.branding.sidenav.subtext,
    fontWeight: '500',
}));

export const FoldersTreeWrapper = styled(
    'div',
    forwardRef
)(() => ({
    width: '100%',
    height: '100%',
}));

export const MaintenanceBanner = styled('div')(({ theme }) => ({
    height: '24px',
    fontSize: '12px',
    backgroundColor: theme.colors.error.w700,
    color: theme.colors.error.w50,
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));
